const DEFAULT_LIMITS = Object.freeze({
  local: 10,
  competitive: 50,
});

const SAFE_PERIOD = "calendar_month";
const SAFE_OVERAGE_BEHAVIOR = "blocked";

function objectValue(value) {
  return value && typeof value === "object" ? value : {};
}

function normalizeAccountType(value) {
  const normalized = String(value || "").trim().toLowerCase().replace(/[ -]+/g, "_");
  return normalized === "local" || normalized === "competitive" || normalized === "enterprise" ? normalized : "unknown";
}

function finiteLimit(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 && numeric <= 100000 ? Math.floor(numeric) : null;
}

function configuredCandidate(source, sourceName) {
  const value = objectValue(source);
  const limit = finiteLimit(value.monthlyContentBriefLimit ?? value.monthly_content_brief_limit ?? value.contentBriefLimit ?? value.content_brief_limit);
  const enabled = value.enabled === undefined && value.contentBriefEnabled === undefined && value.content_brief_enabled === undefined
    ? true
    : Boolean(value.enabled ?? value.contentBriefEnabled ?? value.content_brief_enabled);
  if (limit === null) return null;
  return {
    limit,
    enabled,
    source: sourceName,
    billingResetPeriod: String(value.billingResetPeriod || value.billing_reset_period || SAFE_PERIOD),
    overageBehavior: String(value.overageBehavior || value.overage_behavior || SAFE_OVERAGE_BEHAVIOR),
  };
}

export function resolveContentBriefQuota(input = {}) {
  const source = objectValue(input);
  const account = objectValue(source.account);
  const accountType = normalizeAccountType(source.accountType ?? source.account_type ?? account.accountType ?? account.account_type);
  const used = Math.max(0, Math.floor(Number(source.used ?? source.successfulGenerationsThisMonth ?? source.successful_generations_this_month ?? 0) || 0));
  const localConfig = objectValue(account.local_config);
  const entitlement = source.entitlementOverride ?? source.entitlement_override ?? source.entitlement ?? account.entitlementOverride ?? account.entitlement_override ?? account.entitlement ?? localConfig.contentBriefEntitlement ?? localConfig.content_brief_entitlement;
  const plan = source.planDefault ?? source.plan_default ?? source.planConfig ?? source.plan_config ?? source.packageDefault ?? source.package_default ?? source.packageConfig ?? source.package_config ?? source.plan ?? source.package ?? account.planDefault ?? account.plan_default ?? account.packageDefault ?? account.plan ?? account.package ?? localConfig.contentBriefPlan ?? localConfig.content_brief_plan;
  const accountTypeDefault = source.accountTypeDefault ?? source.account_type_default;

  const candidates = [
    [entitlement, "entitlement_override"],
    [plan, "plan_default"],
  ];
  if (accountType === "local" || accountType === "competitive") {
    candidates.push([{ monthlyContentBriefLimit: DEFAULT_LIMITS[accountType], enabled: true }, "account_type_default"]);
  } else if (accountTypeDefault) {
    candidates.push([accountTypeDefault, "account_type_default"]);
  }

  let configuration = null;
  for (const [candidate, sourceName] of candidates) {
    if (candidate === undefined || candidate === null) continue;
    configuration = configuredCandidate(candidate, sourceName);
    if (configuration) break;
  }

  if (!configuration) {
    return {
      status: "needs_configuration",
      enabled: false,
      accountType,
      limit: null,
      used,
      remaining: 0,
      billingResetPeriod: SAFE_PERIOD,
      overageBehavior: SAFE_OVERAGE_BEHAVIOR,
      source: "safe_fallback",
      message: accountType === "enterprise" ? "Content Brief quota is configured by plan." : "Content Brief quota needs configuration.",
    };
  }

  if (!configuration.enabled) {
    return {
      ...configuration,
      status: "disabled",
      accountType,
      used,
      remaining: 0,
      message: "Content Brief generation is disabled for this plan.",
    };
  }

  const remaining = Math.max(0, configuration.limit - used);
  const exhausted = remaining === 0;
  const modeLabel = accountType === "local" ? "Local" : accountType === "competitive" ? "Competitive" : "Content Brief";
  return {
    ...configuration,
    status: exhausted ? "exhausted" : "configured",
    accountType,
    used,
    remaining,
    message: exhausted
      ? "Monthly Content Brief limit reached"
      : `${modeLabel} includes ${configuration.limit} per month`,
  };
}

export function canGenerateContentBrief(quota, actionBriefReady) {
  return Boolean(actionBriefReady && quota && quota.status === "configured" && quota.remaining > 0);
}

export function recordSuccessfulContentBriefGeneration(quota, idempotencyKey, successfulKeys = []) {
  const current = quota && typeof quota === "object" ? quota : resolveContentBriefQuota();
  const seen = new Set(Array.isArray(successfulKeys) ? successfulKeys : []);
  if (seen.has(idempotencyKey)) return { quota: current, counted: false };
  if (!canGenerateContentBrief(current, true)) return { quota: current, counted: false };
  const nextUsed = current.used + 1;
  return {
    counted: true,
    quota: { ...current, used: nextUsed, remaining: Math.max(0, current.limit - nextUsed), status: nextUsed >= current.limit ? "exhausted" : "configured" },
  };
}

export { DEFAULT_LIMITS };
